import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { VfxGradientAtlas } from "../textures/vfx_gradient_atlas.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../types/core_types.js";

export const ErodingBillboardLayout = {
  aPosRot:      AttributeSize.Vec4, // xyz: start_pos, w: rotation
  aVelGrad:     AttributeSize.Vec4, // xyz: velocity, w: gradient_row
  aScaleParams: AttributeSize.Vec4, // start_scale, end_scale, deceleration_power, flip_flags (1=flip_x, 2=flip_y)
  aTimeParams:  AttributeSize.Vec4, // start_time, duration, dissolve_start_progress, dissolve_softness
  aMainUv:      AttributeSize.Vec4, // min_x, min_y, max_x, max_y
  aNoiseUv:     AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export abstract class VfxErodingBillboardBatchBase extends InstanceBatch<typeof ErodingBillboardLayout>
{
  private static cached_geometry: THREE.BufferGeometry | null = null;

  public constructor( blending: THREE.Blending, max_instances: number, render_order: number )
  {
    const base_geometry = VfxErodingBillboardBatchBase.getGeometry();

    let atlas_texture: THREE.Texture | null = null;
    let gradient_texture: THREE.Texture | null = null;
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
    }
    catch ( _e ) {}
    try
    {
      gradient_texture = VfxGradientAtlas.getTexture();
    }
    catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:            { value: 0.0 },
        uTexture:         { value: atlas_texture },
        uGradientTexture: { value: gradient_texture },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosRot;
        attribute vec4 aVelGrad;
        attribute vec4 aScaleParams;
        attribute vec4 aTimeParams;
        attribute vec4 aMainUv;
        attribute vec4 aNoiseUv;

        varying vec2 vMainAtlasUv;
        varying vec2 vNoiseAtlasUv;
        varying vec3 vThresholdParams;
        varying float vFadeIn;
        varying float vProgress;
        varying float vGradientY;

        void main() {
          float start_time = aTimeParams.x;
          float duration = aTimeParams.y;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          vProgress = progress;
          vGradientY = aVelGrad.w;

          // Dissolve parameters & branchless mask setup
          float dissolve_start = aTimeParams.z;
          float dissolve_softness = aTimeParams.w;
          float dissolve_active = progress >= dissolve_start ? 1.0 : 0.0;
          float dissolve_t = clamp( ( progress - dissolve_start ) / max( 1.0 - dissolve_start, 0.001 ), 0.0, 1.0 );
          float threshold = dissolve_t * ( 1.0 + dissolve_softness );
          vThresholdParams = vec3( threshold - dissolve_softness, threshold, dissolve_active );
          vFadeIn = clamp( progress / 0.08, 0.0, 1.0 );

          // Atlas UV precomputation in vertex shader
          vec2 noise_uv = uv;
          float flip_flags = aScaleParams.w;
          if ( flip_flags >= 2.0 ) {
            noise_uv.y = 1.0 - noise_uv.y;
            flip_flags -= 2.0;
          }
          if ( flip_flags >= 1.0 ) {
            noise_uv.x = 1.0 - noise_uv.x;
          }

          vMainAtlasUv = mix( aMainUv.xy, aMainUv.zw, uv );
          vNoiseAtlasUv = mix( aNoiseUv.xy, aNoiseUv.zw, noise_uv );

          float deceleration_power = aScaleParams.z;
          float ease_dist = 1.0 - pow( 1.0 - progress, deceleration_power );
          vec3 computed_pos = aPosRot.xyz + aVelGrad.xyz * duration * ease_dist;

          float scale_progress = 1.0 - pow( 1.0 - progress, 1.8 );
          float current_scale = mix( aScaleParams.x, aScaleParams.y, scale_progress );

          float rotation = aPosRot.w;
          float cos_r = cos( rotation );
          float sin_r = sin( rotation );
          vec2 rotated_pos = vec2(
            position.x * cos_r - position.y * sin_r,
            position.x * sin_r + position.y * cos_r
          ) * current_scale;

          vec3 billboard_local = vec3( rotated_pos, 0.0 );
          vec4 mv_position = modelViewMatrix * vec4( computed_pos, 1.0 );
          mv_position.xyz += billboard_local;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform sampler2D uGradientTexture;

        varying vec2 vMainAtlasUv;
        varying vec2 vNoiseAtlasUv;
        varying vec3 vThresholdParams;
        varying float vFadeIn;
        varying float vProgress;
        varying float vGradientY;

        void main() {
          vec4 main_tex = texture2D( uTexture, vMainAtlasUv );
          float noise = texture2D( uTexture, vNoiseAtlasUv ).r;

          float raw_mask = smoothstep( vThresholdParams.x, vThresholdParams.y, noise );
          float dissolve_mask = mix( 1.0, raw_mask, vThresholdParams.z );

          vec4 gradient_val = texture2D( uGradientTexture, vec2( vProgress, vGradientY ) );
          vec3 particle_color = gradient_val.rgb;

          // Яскрава палаюча кромка по краю розчинення (беремо колір з початку градієнту)
          vec3 burn_color = texture2D( uGradientTexture, vec2( 0.005, vGradientY ) ).rgb;
          float burn_edge = smoothstep( 0.0, 0.25, dissolve_mask ) * ( 1.0 - smoothstep( 0.25, 0.7, dissolve_mask ) );
          particle_color += burn_color * ( burn_edge * 0.7 );

          float final_alpha = main_tex.a * dissolve_mask * vFadeIn * gradient_val.a;

          gl_FragColor = vec4( particle_color * main_tex.rgb, final_alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: blending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: max_instances, layout: ErodingBillboardLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = render_order;
  }

  private static getGeometry(): THREE.BufferGeometry
  {
    if ( VfxErodingBillboardBatchBase.cached_geometry )
      return VfxErodingBillboardBatchBase.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxErodingBillboardBatchBase.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    main_tex_id: ResourceID,
    noise_tex_id: ResourceID,
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    start_time: number,
    duration: number,
    dissolve_start_progress: number,
    dissolve_softness: number,
    rotation: number = Math.random() * Math.PI * 2,
    deceleration_power: number = 2.2
  ): number
  {
    return this.spawnParticleInternal(
      main_tex_id,
      noise_tex_id,
      gradient_row,
      start_pos,
      velocity,
      start_scale,
      end_scale,
      start_time,
      duration,
      dissolve_start_progress,
      dissolve_softness,
      rotation,
      deceleration_power
    );
  }

  public spawnTrailParticle(
    main_tex_id: ResourceID,
    noise_tex_id: ResourceID,
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    size: THREE.Vector2 | number,
    start_time: number,
    duration: number
  ): number
  {
    const scale: number = typeof size === "number" ? size : ( size?.x ?? 1.0 );
    return this.spawnParticleInternal(
      main_tex_id,
      noise_tex_id,
      gradient_row,
      start_pos,
      velocity,
      scale,
      scale,
      start_time,
      duration,
      1.0, // dissolve_start_progress = 1.0 (clean particle, no erosion)
      0.1,
      Math.random() * Math.PI * 2,
      1.0  // deceleration_power = 1.0 (constant speed, linear motion)
    );
  }

  private spawnParticleInternal(
    main_tex_id: ResourceID,
    noise_tex_id: ResourceID,
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    start_time: number,
    duration: number,
    dissolve_start_progress: number,
    dissolve_softness: number,
    rotation: number = Math.random() * Math.PI * 2,
    deceleration_power: number = 2.2
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
    {
      try { mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}
    }
    if ( !mat.uniforms.uGradientTexture.value )
    {
      try { mat.uniforms.uGradientTexture.value = VfxGradientAtlas.getTexture(); } catch ( _e ) {}
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    let main_uv = { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };
    let noise_uv = { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };
    try {
      main_uv = Vfx3DGlobalAtlas.getUVRect( main_tex_id );
      noise_uv = Vfx3DGlobalAtlas.getUVRect( noise_tex_id );
    } catch (_e) {}

    const pos_rot_attr      = this.geometry.getAttribute( "aPosRot" ).array as Float32Array;
    const vel_grad_attr     = this.geometry.getAttribute( "aVelGrad" ).array as Float32Array;
    const scale_params_attr = this.geometry.getAttribute( "aScaleParams" ).array as Float32Array;
    const time_params_attr  = this.geometry.getAttribute( "aTimeParams" ).array as Float32Array;
    const main_uv_attr      = this.geometry.getAttribute( "aMainUv" ).array as Float32Array;
    const noise_uv_attr     = this.geometry.getAttribute( "aNoiseUv" ).array as Float32Array;

    pos_rot_attr[ idx * 4 ]     = start_pos.x;
    pos_rot_attr[ idx * 4 + 1 ] = start_pos.y;
    pos_rot_attr[ idx * 4 + 2 ] = start_pos.z;
    pos_rot_attr[ idx * 4 + 3 ] = rotation;

    vel_grad_attr[ idx * 4 ]     = velocity.x;
    vel_grad_attr[ idx * 4 + 1 ] = velocity.y;
    vel_grad_attr[ idx * 4 + 2 ] = velocity.z;
    vel_grad_attr[ idx * 4 + 3 ] = gradient_row;

    const flip_flags = ( Math.random() < 0.5 ? 1.0 : 0.0 ) + ( Math.random() < 0.5 ? 2.0 : 0.0 );
    scale_params_attr[ idx * 4 ]     = start_scale;
    scale_params_attr[ idx * 4 + 1 ] = end_scale;
    scale_params_attr[ idx * 4 + 2 ] = deceleration_power;
    scale_params_attr[ idx * 4 + 3 ] = flip_flags;

    time_params_attr[ idx * 4 ]     = start_time;
    time_params_attr[ idx * 4 + 1 ] = duration;
    time_params_attr[ idx * 4 + 2 ] = dissolve_start_progress;
    time_params_attr[ idx * 4 + 3 ] = dissolve_softness;

    main_uv_attr[ idx * 4 ]     = main_uv.min_x;
    main_uv_attr[ idx * 4 + 1 ] = main_uv.min_y;
    main_uv_attr[ idx * 4 + 2 ] = main_uv.max_x;
    main_uv_attr[ idx * 4 + 3 ] = main_uv.max_y;

    noise_uv_attr[ idx * 4 ]     = noise_uv.min_x;
    noise_uv_attr[ idx * 4 + 1 ] = noise_uv.min_y;
    noise_uv_attr[ idx * 4 + 2 ] = noise_uv.max_x;
    noise_uv_attr[ idx * 4 + 3 ] = noise_uv.max_y;

    this.geometry.getAttribute( "aPosRot" ).needsUpdate      = true;
    this.geometry.getAttribute( "aVelGrad" ).needsUpdate     = true;
    this.geometry.getAttribute( "aScaleParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aTimeParams" ).needsUpdate  = true;
    this.geometry.getAttribute( "aMainUv" ).needsUpdate      = true;
    this.geometry.getAttribute( "aNoiseUv" ).needsUpdate     = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}

export class VfxErodingBillboardAdditiveBatch extends VfxErodingBillboardBatchBase
{
  constructor() {
    super( THREE.AdditiveBlending, 1200, 29 );
  }
}

export class VfxErodingBillboardNormalBatch extends VfxErodingBillboardBatchBase
{
  constructor() {
    super( THREE.NormalBlending, 2800, 28 );
  }
}
